import { z } from "zod";
import { IntakeFieldStatisticsSchema } from "./organization";

/**
 * Schémas Zod pour les formulaires personnalisables.
 * Distincts du IntakeForm de l'organisation (qui sert pour le booking/portail).
 */

const FieldIdSchema = z.string().min(1).max(80);

/** Stats agrégées (fenêtre envois / vue globale) — même schéma que l’intake. */
export const FormFieldStatisticsSchema = IntakeFieldStatisticsSchema;
export type FormFieldStatistics = z.infer<typeof FormFieldStatisticsSchema>;

const statisticsOptional = z.object({
  statistics: FormFieldStatisticsSchema.optional(),
});

/** Logique conditionnelle : afficher ce champ uniquement si la condition est satisfaite. */
export const FormShowIfSchema = z.object({
  fieldId: FieldIdSchema,
  operator: z.enum(["equals", "notEquals", "in"]),
  value: z.union([z.string(), z.number(), z.array(z.string())]),
});
export type FormShowIf = z.infer<typeof FormShowIfSchema>;

const FormFieldBaseShape = {
  id: FieldIdSchema,
  label: z.string().min(1).max(500),
  required: z.boolean().default(false),
  helpText: z.string().max(1000).optional(),
  showIf: FormShowIfSchema.optional(),
};

export const FormTextFieldSchema = z
  .object({
    type: z.literal("text"),
    ...FormFieldBaseShape,
    placeholder: z.string().max(200).optional(),
  })
  .merge(statisticsOptional);
export const FormEmailFieldSchema = z
  .object({
    type: z.literal("email"),
    ...FormFieldBaseShape,
  })
  .merge(statisticsOptional);
export const FormPhoneFieldSchema = z
  .object({
    type: z.literal("phone"),
    ...FormFieldBaseShape,
  })
  .merge(statisticsOptional);
export const FormNumberFieldSchema = z
  .object({
    type: z.literal("number"),
    ...FormFieldBaseShape,
    min: z.number().optional(),
    max: z.number().optional(),
  })
  .merge(statisticsOptional);
export const FormDateFieldSchema = z
  .object({
    type: z.literal("date"),
    ...FormFieldBaseShape,
  })
  .merge(statisticsOptional);
export const FormTextareaFieldSchema = z
  .object({
    type: z.literal("textarea"),
    ...FormFieldBaseShape,
    placeholder: z.string().max(200).optional(),
  })
  .merge(statisticsOptional);
export const FormSelectFieldSchema = z
  .object({
    type: z.literal("select"),
    ...FormFieldBaseShape,
    options: z.array(z.string().min(1)).min(1).max(50),
  })
  .merge(statisticsOptional);
export const FormRadioFieldSchema = z
  .object({
    type: z.literal("radio"),
    ...FormFieldBaseShape,
    options: z.array(z.string().min(1)).min(2).max(20),
  })
  .merge(statisticsOptional);
export const FormCheckboxFieldSchema = z
  .object({
    type: z.literal("checkbox"),
    ...FormFieldBaseShape,
    options: z.array(z.string().min(1)).min(1).max(50),
  })
  .merge(statisticsOptional);

/** Échelle de Likert : un nombre entier de points avec libellés gauche/droite, et libellés optionnels par point. */
export const FormLikertFieldSchema = z
  .object({
    type: z.literal("likert"),
    ...FormFieldBaseShape,
    scaleSize: z.number().int().min(2).max(11),
    leftLabel: z.string().max(80).optional(),
    rightLabel: z.string().max(80).optional(),
    pointLabels: z.array(z.string().max(80)).max(11).optional(),
  })
  .merge(statisticsOptional);

export const FormDocumentFieldSchema = z
  .object({
    type: z.literal("document"),
    ...FormFieldBaseShape,
  })
  .merge(statisticsOptional);

/** Section : titre/séparateur visuel, sans réponse. */
export const FormSectionFieldSchema = z.object({
  type: z.literal("section"),
  id: FieldIdSchema,
  label: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
});

export const FormFieldSchema = z.discriminatedUnion("type", [
  FormTextFieldSchema,
  FormEmailFieldSchema,
  FormPhoneFieldSchema,
  FormNumberFieldSchema,
  FormDateFieldSchema,
  FormTextareaFieldSchema,
  FormSelectFieldSchema,
  FormRadioFieldSchema,
  FormCheckboxFieldSchema,
  FormLikertFieldSchema,
  FormDocumentFieldSchema,
  FormSectionFieldSchema,
]);
export type FormField = z.infer<typeof FormFieldSchema>;
export type FormFieldType = FormField["type"];

/** Tous les types de champs qui produisent une réponse (excluent les sections). */
export const FORM_ANSWERABLE_FIELD_TYPES = [
  "text",
  "email",
  "phone",
  "number",
  "date",
  "textarea",
  "select",
  "radio",
  "checkbox",
  "likert",
  "document",
] as const;

export const FormFieldsSchema = z
  .array(FormFieldSchema)
  .max(500)
  .superRefine((fields, ctx) => {
    const declaredBefore = new Set<string>();
    for (let i = 0; i < fields.length; i++) {
      const f = fields[i];
      if (!f) continue;
      if (declaredBefore.has(f.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Identifiant dupliqué : ${f.id}`,
          path: [i, "id"],
        });
      }
      if ("showIf" in f && f.showIf) {
        if (
          !declaredBefore.has(f.showIf.fieldId) ||
          f.showIf.fieldId === f.id
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `showIf.fieldId doit référencer un champ existant déclaré avant celui-ci`,
            path: [i, "showIf", "fieldId"],
          });
        }
      }
      declaredBefore.add(f.id);
    }
  });
export type FormFields = z.infer<typeof FormFieldsSchema>;

/** Sous-échelle de scoring : agrège un sous-ensemble d'items. */
const FormScoringScaleBaseSchema = z.object({
  id: z.string().min(1).max(80),
  label: z.string().max(200),
  fieldIds: z.array(FieldIdSchema),
  aggregation: z.enum(["sum", "mean"]),
  /** Mapping pour transformer une option textuelle en valeur numérique (likert/select). */
  valueMapping: z.record(z.string(), z.number()).optional(),
  /** Plages d'interprétation (ex: "Léger", "Modéré", "Sévère"). */
  interpretation: z
    .array(
      z.object({
        min: z.number(),
        max: z.number(),
        label: z.string().max(200),
      }),
    )
    .max(20)
    .optional(),
});

export const FormScoringScaleSchema = FormScoringScaleBaseSchema.superRefine(
  (scale, ctx) => {
    if (scale.label.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Libellé de sous-échelle requis",
        path: ["label"],
      });
    }
    if (scale.fieldIds.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Sélectionnez au moins un champ pour la sous-échelle",
        path: ["fieldIds"],
      });
    }
    if (scale.interpretation) {
      scale.interpretation.forEach((row, i) => {
        if (row.label.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Libellé de plage requis",
            path: ["interpretation", i, "label"],
          });
        }
      });
    }
  },
);
export type FormScoringScale = z.infer<typeof FormScoringScaleSchema>;

export const FormScoringSchema = z.object({
  scales: z.array(FormScoringScaleSchema).max(20),
});
export type FormScoring = z.infer<typeof FormScoringSchema>;

export const FormTemplateInputSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  fields: FormFieldsSchema,
  scoring: FormScoringSchema.optional(),
});
export type FormTemplateInput = z.infer<typeof FormTemplateInputSchema>;

/** Snapshot embarqué dans une FormSubmission (figé au moment de l'envoi). */
export const FormTemplateSnapshotSchema = z.object({
  title: z.string(),
  description: z.string().nullable().optional(),
  fields: FormFieldsSchema,
  scoring: FormScoringSchema.nullable().optional(),
});
export type FormTemplateSnapshot = z.infer<typeof FormTemplateSnapshotSchema>;

/** Une réponse peut être une string, un nombre, ou un tableau (checkbox multi). */
export const FormResponseValueSchema = z.union([
  z.string(),
  z.number(),
  z.array(z.string()),
  z.boolean(),
  z.null(),
]);
export type FormResponseValue = z.infer<typeof FormResponseValueSchema>;

export const FormResponsesSchema = z.record(z.string(), FormResponseValueSchema);
export type FormResponses = z.infer<typeof FormResponsesSchema>;

export const FormSubmissionStatusSchema = z.enum([
  "SENT",
  "OPENED",
  "COMPLETED",
  "EXPIRED",
  "CANCELLED",
]);
export type FormSubmissionStatusValue = z.infer<typeof FormSubmissionStatusSchema>;

export const FormScoreResultSchema = z.object({
  scaleId: z.string(),
  label: z.string(),
  value: z.number(),
  interpretation: z.string().optional(),
  itemCount: z.number().int(),
});
export type FormScoreResult = z.infer<typeof FormScoreResultSchema>;

export const FormScoresSchema = z.object({
  scales: z.array(FormScoreResultSchema),
});
export type FormScores = z.infer<typeof FormScoresSchema>;
